import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { 
  Upload, 
  UserPlus, 
  Loader2,
  CheckCircle2,
  AlertCircle,
  FileSpreadsheet,
  Users
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const ImportarClientes = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  
  // Para agregar uno solo
  const [nombre, setNombre] = useState('');
  const [telefono, setTelefono] = useState('');
  const [participaciones, setParticipaciones] = useState('1');
  
  // Para importar masivo
  const [textoMasivo, setTextoMasivo] = useState('');

  const handleAgregarUno = async (e) => {
    e.preventDefault();
    
    if (!nombre.trim() || !telefono.trim()) {
      toast.error('Completá nombre y teléfono');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/clientes/agregar`, {
        nombre: nombre.trim(),
        telefono: telefono.trim(),
        participaciones: parseInt(participaciones) || 1
      });
      
      toast.success(response.data.nuevo ? 'Cliente agregado' : 'Cliente actualizado');
      setNombre('');
      setTelefono('');
      setParticipaciones('1');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al agregar cliente');
    } finally {
      setLoading(false);
    }
  };

  const handleImportarMasivo = async () => {
    if (!textoMasivo.trim()) {
      toast.error('Pegá la lista de clientes');
      return;
    }
    
    // Parsear el texto - formato: nombre, telefono, participaciones (una línea por cliente)
    const lineas = textoMasivo.trim().split('\n');
    const clientes = [];
    
    for (const linea of lineas) {
      const partes = linea.split(/[,;\t]+/).map(p => p.trim());
      if (partes.length >= 2) {
        clientes.push({
          nombre: partes[0],
          telefono: partes[1],
          participaciones: parseInt(partes[2]) || 1
        });
      }
    }
    
    if (clientes.length === 0) {
      toast.error('No se encontraron clientes válidos. Usá formato: nombre, teléfono, participaciones');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/clientes/importar`, { clientes });
      setResultado(response.data);
      toast.success(`${response.data.total} clientes procesados`);
      setTextoMasivo('');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al importar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold">Importar Clientes</h2>
        <p className="text-gray-600">Cargá tu lista de clientes para poder hacer sorteos</p>
      </div>

      {resultado && (
        <Card className="bg-green-50 border-green-300">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="text-green-600" size={24} />
              <div>
                <p className="font-semibold text-green-800">¡Importación completada!</p>
                <p className="text-green-700">
                  {resultado.importados} nuevos, {resultado.actualizados} actualizados
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Agregar uno */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="text-blue-600" size={20} />
              Agregar Cliente
            </CardTitle>
            <CardDescription>Agregá un cliente de forma manual</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAgregarUno} className="space-y-4">
              <div>
                <Label>Nombre *</Label>
                <Input
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  placeholder="Ej: Juan Pérez"
                />
              </div>
              <div>
                <Label>Teléfono *</Label>
                <Input
                  value={telefono}
                  onChange={(e) => setTelefono(e.target.value)}
                  placeholder="Ej: +54 9 11 1234-5678"
                />
              </div>
              <div>
                <Label>Participaciones (números en sorteos)</Label>
                <Input
                  type="number"
                  value={participaciones}
                  onChange={(e) => setParticipaciones(e.target.value)}
                  placeholder="1"
                  min="1"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full"
                disabled={loading}
              >
                {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : <UserPlus className="mr-2" size={18} />}
                Agregar Cliente
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Importar masivo */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="text-green-600" size={20} />
              Importar Lista
            </CardTitle>
            <CardDescription>Pegá tu lista de clientes desde Excel o texto</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 p-3 rounded-lg text-sm">
              <p className="font-semibold text-blue-800 mb-1">Formato por línea:</p>
              <code className="text-blue-700">Nombre, Teléfono, Participaciones</code>
              <p className="text-blue-600 mt-2">Ejemplo:</p>
              <pre className="text-blue-700 text-xs mt-1">
María García, +5491112345678, 5{'\n'}
Juan López, +5491187654321, 3{'\n'}
Ana Rodríguez, +5491155555555, 8
              </pre>
            </div>
            
            <div>
              <Label>Lista de Clientes</Label>
              <Textarea
                value={textoMasivo}
                onChange={(e) => setTextoMasivo(e.target.value)}
                placeholder="Pegá tu lista acá..."
                className="h-48 font-mono text-sm"
              />
            </div>
            
            <Button 
              onClick={handleImportarMasivo}
              className="w-full bg-green-600 hover:bg-green-700"
              disabled={loading}
            >
              {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : <Upload className="mr-2" size={18} />}
              Importar Clientes
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Instrucciones */}
      <Card className="bg-yellow-50 border-yellow-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-yellow-600 flex-shrink-0" size={24} />
            <div>
              <p className="font-semibold text-yellow-800">¿Cómo obtener tu lista?</p>
              <ol className="text-yellow-700 text-sm mt-2 list-decimal list-inside space-y-1">
                <li>Andá a tu proyecto anterior donde tenés los clientes</li>
                <li>En la sección "Clientes", hacé clic en "Descargar Excel"</li>
                <li>Abrí el archivo y copiá las columnas: Nombre, Teléfono, Números Pagados</li>
                <li>Pegá la lista acá arriba</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-4">
        <Button variant="outline" onClick={() => navigate('/admin/clientes')}>
          <Users className="mr-2" size={18} />
          Ver Clientes
        </Button>
      </div>
    </div>
  );
};

export default ImportarClientes;
